import { useRef, useState } from 'react';
import type { DemoScript, FingerKey, Fx, Level, Pt, RoundProps, ViewProps } from '../kit.js';
import { BAD, BOARD, BoardFrame, INK, PALETTE, WARN, easier, q, say, seeded, thirtyGame, useRound, useSvgId } from '../kit.js';
import type { Era } from '../../art.js';
import { ERA_SKIN } from '../../art.js';
import { CLAY, GROUND, LEAF_D, clamp, demoRound, feedbackTo, mix, playDemo } from './garden.js';
import type { Feedback } from './garden.js';

/**
 * BIOTECHNICIAN · KEEP THEM ALIVE — tap a plant to water it; keep every one above the red line.
 *
 * Potted plants stand on a shelf, each drying out at its own pace; the gauge under each pot shows
 * how much water it has, and the plant itself droops and pales as it dries. A tap anywhere on a
 * plant's place waters it: a watering can tips over it and it perks up. The round lasts twelve
 * seconds, counted down by the bar along the top; a plant that reaches the red line is the mistake
 * (it is rescued back to half and the round goes on). Round 1 has four plants drying slowly, round
 * 2 four a little quicker, round 3 five quicker still, on two shelves.
 */

export interface Bed {
  plants: number;
  /** How fast each plant dries, per second, from … to. */
  rate: readonly [number, number];
  /** How full each starts, from … to. */
  start: readonly [number, number];
  secs: number;
}

/** Easier since 6 Oct 2026 (kit `easier`): round 1 as before, round 2 halfway to the old round 2, round 3 the old round 2. */
export const BEDS: readonly Bed[] = easier(
  [
    { plants: 4, rate: [0.05, 0.08], start: [0.55, 0.95], secs: 12 },
    { plants: 5, rate: [0.06, 0.09], start: [0.5, 0.95], secs: 12 },
  ],
);

/** The red line. */
export const WILT = 0.22;
/** What one tap gives. */
export const SIP = 0.4;
/** Where a wilted plant is rescued back to. */
export const RESCUE = 0.5;

export type Species = 'flower' | 'leafy' | 'bud';
const SPECIES: readonly Species[] = ['flower', 'leafy', 'bud'];

export interface Plant {
  rate: number;
  w: number;
  species: Species;
  /** Which way it leans as it droops. */
  lean: 1 | -1;
}

/** A round's shelf: each plant its own pace, the quick ones starting full enough to be noticed in time. */
export function plantShelf(rand: () => number, level: Level): Plant[] {
  const b = BEDS[level]!;
  const offset = Math.floor(rand() * SPECIES.length);
  const paces = Array.from({ length: b.plants }, (_, i) => b.rate[0] + ((b.rate[1] - b.rate[0]) * (i + rand() * 0.8)) / b.plants);
  for (let i = paces.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rand() * (i + 1));
    [paces[i], paces[j]] = [paces[j]!, paces[i]!];
  }
  // Each leans as it droops, the ones at either end of a shelf inwards, so none bends off the board.
  const ends = new Set(rowsFor(b.plants).flatMap((n, r, rows) => {
    const first = rows.slice(0, r).reduce((a, x) => a + x, 0);
    return [first, first + n - 1];
  }));
  const firsts = new Set(rowsFor(b.plants).map((_, r, rows) => rows.slice(0, r).reduce((a, x) => a + x, 0)));
  return paces.map((rate, i) => {
    const w = b.start[0] + rand() * (b.start[1] - b.start[0]);
    const lean: 1 | -1 = firsts.has(i) ? 1 : ends.has(i) ? -1 : rand() < 0.5 ? -1 : 1;
    return { rate, w: Math.min(1, Math.max(w, WILT + rate * 3.5)), species: SPECIES[(i + offset) % SPECIES.length]!, lean };
  });
}

/** One step of drying: every plant loses its pace × `dt`; any that reach the line are rescued. */
export function dryStep(levels: readonly number[], rates: readonly number[], dt: number): { levels: number[]; wilted: number[] } {
  const wilted: number[] = [];
  const out = levels.map((w, i) => {
    const next = w - (rates[i] ?? 0) * dt;
    if (next <= WILT) {
      wilted.push(i);
      return RESCUE;
    }
    return next;
  });
  return { levels: out, wilted };
}

export const watered = (w: number) => Math.min(1, w + SIP);

/** How lively a plant looks at water level `w`: 0 at the line, 1 full. */
export const vigour = (w: number) => clamp((w - WILT) / (1 - WILT));

/** The shelves: up to four plants on one, more on two. */
export function rowsFor(n: number): number[] {
  return n <= 4 ? [n] : [Math.ceil(n / 2), Math.floor(n / 2)];
}

const W = BOARD.w;
const H = BOARD.h;
/** The watering can shows this long after a tap. */
const WATER_MS = 600;
/** A wilted plant's gauge shows red this long; "one wilted" shows a little longer. */
const WILT_SHOW_MS = 900;
const WILTED_MS = 1_300;

/** Each plant's place on the board, in units: its shelf's share of the board, row by row. */
export function placesFor(n: number): { x: number; y: number; w: number; h: number }[] {
  const rows = rowsFor(n);
  const rowH = H / rows.length;
  return rows.flatMap((count, r) => Array.from({ length: count }, (_, c) => ({ x: (c * W) / count, y: r * rowH, w: W / count, h: rowH })));
}

/** The plant whose place is under a point, or −1. */
export function plantAt(n: number, p: Pt): number {
  return placesFor(n).findIndex((c) => p.x >= c.x && p.x < c.x + c.w && p.y >= c.y && p.y < c.y + c.h);
}

/** Everything the shelf shows. */
export interface WaterView {
  /** The game clock, ms. */
  t: number;
  /** The plants: water (0–1), kind (0 flower, 1 leafy, 2 bud), which way it leans, ms since watered, ms since it wilted (−1: not lately). */
  p: readonly (readonly [w: number, kind: number, lean: number, wet: number, wilt: number])[];
  /** Seconds left, and the round's length. */
  left: number;
  secs: number;
  /** 0 keeping them up, 1 one just wilted, 2 time is up and all are alive. */
  st: 0 | 1 | 2;
  fx?: Fx | undefined;
}

/** A round on the shelf, driven by taps (a guest's or the how-to's) and the game clock. */
export function waterRound(level: Level, plants: Plant[], fb: Feedback) {
  const B = BEDS[level]!;
  let startAt: number | null = null;
  const wateredAt = plants.map(() => -1e9);
  const wiltAt = plants.map(() => -1e9);
  let lastWilt = -1e9;
  let won = false;
  return {
    input: {
      down(p: Pt, at: number) {
        if (won) return;
        const i = plantAt(plants.length, p);
        const plant = plants[i];
        if (!plant) return;
        plant.w = watered(plant.w);
        wateredAt[i] = at;
        fb.hit();
      },
      move() {},
      up() {},
    },
    step(dt: number, at: number) {
      if (startAt === null) startAt = at;
      if (won) return;
      const { levels, wilted } = dryStep(
        plants.map((p) => p.w),
        plants.map((p) => p.rate),
        dt,
      );
      plants.forEach((p, i) => (p.w = levels[i]!));
      for (const i of wilted) {
        wiltAt[i] = at;
        lastWilt = at;
        fb.miss();
      }
      const f = clamp((at - startAt) / (B.secs * 1000));
      fb.progress(Math.min(0.99, f));
      if (f >= 1) {
        won = true;
        fb.win();
      }
    },
    view(t: number, fx: Fx | undefined): WaterView {
      return {
        t: q(t),
        p: plants.map((p, i) => {
          const wet = t - wateredAt[i]!;
          const wilt = t - wiltAt[i]!;
          return [q(p.w, 2), SPECIES.indexOf(p.species), p.lean, wet < WATER_MS ? q(wet) : -1, wilt < WILT_SHOW_MS ? q(wilt) : -1] as const;
        }),
        left: q(startAt === null ? B.secs : Math.max(0, B.secs - (t - startAt) / 1000), 1),
        secs: B.secs,
        st: won ? 2 : t - lastWilt < WILTED_MS ? 1 : 0,
        fx,
      };
    },
  };
}

/** The water in a gauge, and a drop from the can. */
const WATER = PALETTE[0];
const WATER_HI = mix(PALETTE[0], INK.hi, 0.45);

/** The shelf, from the view state alone. */
function View({ view, era, accent, lang, input }: ViewProps<WaterView>) {
  const id = useSvgId();
  // A demo glides every number between its keys: states are read rounded.
  const st = Math.round(view.st);
  const status =
    st === 2
      ? { text: say(lang, '✓ ALL ALIVE', '✓ ALLE LEVER'), tone: 'ok' as const }
      : st === 1
        ? { text: say(lang, 'ONE WILTED — WATER SOONER', 'EN VISNET – VANN TIDLIGERE'), tone: 'bad' as const }
        : {
            text: say(lang, `KEEP THEM UP · ${Math.ceil(view.left)} S`, `HOLD DEM OPPE · ${Math.ceil(view.left)} S`),
            hint: say(lang, 'TAP TO WATER', 'TRYKK OG VANN'),
          };
  const places = placesFor(view.p.length);

  return (
    <BoardFrame era={era} accent={accent} status={status} fx={view.fx} input={input}>
      <defs>
        <linearGradient id={`${id}pot`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor={CLAY.shade} />
          <stop offset=".35" stopColor={CLAY.lit} />
          <stop offset=".7" stopColor={CLAY.body} />
          <stop offset="1" stopColor={CLAY.deep} />
        </linearGradient>
        <linearGradient id={`${id}water`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor={WATER} />
          <stop offset="1" stopColor={WATER_HI} />
        </linearGradient>
        <linearGradient id={`${id}wood`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#a8744a" />
          <stop offset="1" stopColor="#5a3a1e" />
        </linearGradient>
        <radialGradient id={`${id}sun`} cx=".5" cy="0" r="1">
          <stop offset="0" stopColor={ERA_SKIN[era].haze} stopOpacity=".2" />
          <stop offset="1" stopColor={ERA_SKIN[era].haze} stopOpacity="0" />
        </radialGradient>
      </defs>
      <rect x="0" y="0" width={W} height={H} fill={`url(#${id}sun)`} />
      {/* the time left, a thin bar along the top */}
      <rect x="10" y="8" width={W - 20} height="4" rx="2" fill={INK.hi} opacity=".1" />
      <rect x="10" y="8" width={((W - 20) * clamp(view.left / view.secs)).toFixed(1)} height="4" rx="2" fill={accent} opacity=".75" />

      {places.map((c, i) => {
        const [w, kind, lean, wet, wilt] = view.p[i]!;
        const shelfY = c.y + c.h - 24;
        const scale = clamp((c.h - 30) / 140, 0.5, 1.45);
        const leans = lean < 0 ? -1 : 1;
        return (
          <g key={i}>
            {/* the shelf */}
            <rect x={c.x} y={shelfY + 2} width={c.w} height="7" fill={`url(#${id}wood)`} />
            <line x1={c.x} y1={shelfY + 2.5} x2={c.x + c.w} y2={shelfY + 2.5} stroke="#f0c890" strokeOpacity=".35" />
            <g transform={`translate(${c.x + c.w / 2} ${shelfY})`}>
              <PlantArt w={w} species={SPECIES[Math.round(kind)] ?? 'flower'} lean={leans} era={era} id={id} scale={scale} />
              {/* the watering can, for a moment after a tap */}
              {wet >= 0 && wet < WATER_MS && (
                <g transform={`translate(${leans * -14 * scale} ${-(128 * scale + 14)})`} opacity={clamp(1.4 - wet / WATER_MS).toFixed(2)}>
                  <WateringCan flip={leans === 1} />
                  {[0, 1, 2].map((d) => {
                    const u = (wet - d * 80) / 500;
                    if (u < 0 || u > 1) return null;
                    return <ellipse key={d} cx={leans * 14 + (d - 1) * 4} cy={10 + 22 * u} rx="1.4" ry="2.4" fill={WATER_HI} opacity={(u < 0.15 ? u / 0.15 : 1 - (u - 0.15) / 0.85).toFixed(2)} />;
                  })}
                </g>
              )}
              {/* the gauge */}
              <g transform="translate(0 11)">
                <rect x="-31" y="0" width="62" height="9" rx="4.5" fill={INK.onFill} stroke={wilt >= 0 ? BAD : INK.text} strokeOpacity={wilt >= 0 ? 1 : 0.3} strokeWidth="1" />
                <rect x="-30" y="1" width={(60 * clamp(w)).toFixed(1)} height="7" rx="3.5" fill={vigour(w) < 0.18 ? WARN : `url(#${id}water)`} />
                <line x1={-30 + 60 * WILT} y1="-2.5" x2={-30 + 60 * WILT} y2="11.5" stroke={BAD} strokeWidth="2" />
              </g>
            </g>
          </g>
        );
      })}
    </BoardFrame>
  );
}

/** A potted plant, its pot's foot at (0, 0), with water `w`. It droops, leans and pales as it dries. */
function PlantArt({ w, species, lean, era, id, scale }: { w: number; species: Species; lean: 1 | -1; era: Era; id: string; scale: number }) {
  const g = GROUND[era];
  const v = vigour(w);
  const dry = Math.pow(1 - v, 1.4);
  const leaf = mix(g.leaf, '#a89648', dry);
  const leafHi = mix(g.leafHi, '#d8c890', dry);
  const stem = mix(g.stem, '#8a7a3c', dry);
  // The stem: upright when watered, bending over as it dries.
  const bend = (1 - v) * 1.15;
  const L = 100;
  const top = { x: lean * Math.sin(bend) * L * 0.42, y: -34 - Math.cos(bend) * L * 0.66 };
  const ctrl = { x: lean * bend * 6, y: -58 };
  const base = { x: 0, y: -30 };
  const at = (t: number) => ({
    x: (1 - t) * (1 - t) * base.x + 2 * (1 - t) * t * ctrl.x + t * t * top.x,
    y: (1 - t) * (1 - t) * base.y + 2 * (1 - t) * t * ctrl.y + t * t * top.y,
  });
  const tangent = (t: number) => {
    const a = at(Math.max(0, t - 0.02));
    const b = at(Math.min(1, t + 0.02));
    return (Math.atan2(b.y - a.y, b.x - a.x) * 180) / Math.PI;
  };
  const tip = at(1);
  const tipAng = tangent(0.98);
  const droop = (1 - v) * 55;
  return (
    <g transform={`scale(${scale.toFixed(3)})`}>
      {/* stem and leaves */}
      <path d={`M0 -30 Q ${ctrl.x.toFixed(1)} ${ctrl.y.toFixed(1)} ${top.x.toFixed(1)} ${top.y.toFixed(1)}`} fill="none" stroke={stem} strokeWidth="3.2" strokeLinecap="round" />
      {[0.3, 0.52, 0.74].map((t, i) => {
        const p = at(t);
        const side = i % 2 === 0 ? 1 : -1;
        const rot = tangent(t) + side * (62 + droop * 0.8);
        const size = species === 'leafy' ? 2.1 - i * 0.2 : 1.6 - i * 0.15;
        return (
          <g key={t} transform={`translate(${p.x.toFixed(1)} ${p.y.toFixed(1)}) rotate(${rot.toFixed(1)}) scale(${size.toFixed(2)})`}>
            <path d={LEAF_D} fill={leaf} stroke={mix(g.stemDark, '#5a4a20', dry)} strokeWidth=".4" />
            <path d="M0.6 0 Q 5 -0.5 9 0" fill="none" stroke={leafHi} strokeWidth=".45" opacity=".8" />
          </g>
        );
      })}
      {/* the head */}
      <g transform={`translate(${tip.x.toFixed(1)} ${tip.y.toFixed(1)}) rotate(${(tipAng + 90).toFixed(1)})`}>
        {species === 'flower' && (
          <g transform={`scale(${(0.75 + v * 0.35).toFixed(3)})`}>
            {Array.from({ length: 8 }, (_, i) => (
              <ellipse key={i} cx="0" cy="-8" rx="3.6" ry="7" transform={`rotate(${i * 45})`} fill={mix('#ff8cc6', '#b08a7a', dry)} stroke={mix('#b0306e', '#6a5040', dry)} strokeWidth=".5" />
            ))}
            <circle r="4.4" fill={mix('#ffd23f', '#a88a3a', dry)} />
          </g>
        )}
        {species === 'leafy' && (
          <g>
            <g transform="rotate(-60) scale(1.7)">
              <path d={LEAF_D} fill={leaf} />
            </g>
            <g transform="rotate(-120) scale(1.7)">
              <path d={LEAF_D} fill={leafHi} />
            </g>
            <circle r="2.2" fill={stem} />
          </g>
        )}
        {species === 'bud' && (
          <g>
            <path d="M0 2 C -9 -2, -9 -14, 0 -19 C 9 -14, 9 -2, 0 2 Z" fill={mix('#ffb347', '#a8865a', dry)} stroke={mix('#a85a10', '#5a4020', dry)} strokeWidth=".7" />
            <path d="M0 2 C -3 -6, -2 -13, 0 -19" fill="none" stroke={mix('#ffe0a0', '#c8b08a', dry)} strokeWidth="1.2" opacity=".7" />
            <path d="M-7 -1 C -6 3, 6 3, 7 -1 L 0 4 Z" fill={leaf} />
          </g>
        )}
      </g>
      {/* the pot */}
      <ellipse cx="0" cy="1.5" rx="22" ry="3.5" fill="#000" opacity=".4" />
      <path d="M-18 -28 L18 -28 L14 -2 Q 13 0 10 0 L-10 0 Q -13 0 -14 -2 Z" fill={`url(#${id}pot)`} stroke={CLAY.edge} strokeWidth=".9" />
      <rect x="-21" y="-33" width="42" height="8" rx="2" fill={CLAY.rim} stroke={CLAY.edge} strokeWidth=".9" />
      <line x1="-19" y1="-31" x2="15" y2="-31" stroke={CLAY.shine} strokeOpacity=".5" strokeWidth="1.2" strokeLinecap="round" />
      <ellipse cx="0" cy="-32" rx="18.5" ry="2.6" fill={mix(CLAY.earth, '#6a5236', dry * 0.8)} />
    </g>
  );
}

function WateringCan({ flip }: { flip: boolean }) {
  return (
    <g transform={`scale(${flip ? -1 : 1} 1) rotate(18)`}>
      <path d="M-14 -8 L6 -8 L8 8 L-12 8 Z" fill="#9fb8c8" stroke="#3a4a58" strokeWidth=".9" />
      <path d="M6 -2 L20 -10 L21 -7 L8 2 Z" fill="#9fb8c8" stroke="#3a4a58" strokeWidth=".9" />
      <path d="M-14 -4 Q -22 -2 -14 6" fill="none" stroke="#3a4a58" strokeWidth="1.8" />
      <path d="M-10 -8 Q -4 -16 4 -8" fill="none" stroke="#3a4a58" strokeWidth="1.8" />
      <line x1="-11" y1="-5" x2="3" y2="-5" stroke="#ffffff" strokeOpacity=".5" strokeWidth="1.2" />
    </g>
  );
}

function Board(props: RoundProps) {
  const { level, lang } = props;
  const fb = useRef<Feedback | null>(null);
  const [game] = useState(() => waterRound(level, plantShelf(seeded(Date.now() + level * 7919), level), feedbackTo(fb)));
  const round = useRound(props, (dt, now) => game.step(dt, now));
  fb.current = round;
  return (
    <View
      view={round.show(game.view(round.now, round.fx))}
      era={props.era}
      accent={props.accent}
      lang={lang}
      input={{ label: say(lang, 'Tap a plant to water it', 'Trykk på en plante for å vanne den'), down: (p) => round.live && game.input.down(p, round.time()) }}
    />
  );
}

/** The how-to's shelf: round 1's four, two of them drying fast. */
const demoShelf = (): Plant[] => [
  { rate: 0.03, w: 0.9, species: 'flower', lean: 1 },
  { rate: 0.09, w: 0.52, species: 'leafy', lean: -1 },
  { rate: 0.03, w: 0.85, species: 'bud', lean: 1 },
  { rate: 0.09, w: 0.72, species: 'flower', lean: -1 },
];

/**
 * The how-to, played by the rules themselves: the plants dry; the finger taps the one drooping
 * most, the can tips over it and it stands up; then another, further along.
 */
function demoWater(): DemoScript<WaterView> {
  const ms = 6_600;
  const finger: FingerKey[] = [
    { at: 0, x: 290, y: 312, hide: true },
    { at: 400, x: 290, y: 312, hide: false },
    { at: 1_300, x: 120, y: 236 },
    { at: 1_900, x: 120, y: 236, down: true },
    { at: 2_020, x: 120, y: 236, down: false },
    { at: 3_500, x: 280, y: 236 },
    { at: 4_200, x: 280, y: 236, down: true },
    { at: 4_320, x: 280, y: 236, down: false },
    { at: 5_300, x: 296, y: 312, hide: true },
  ];
  const round = demoRound();
  const game = waterRound(0, demoShelf(), round);
  const keys = playDemo(
    ms,
    finger,
    round,
    { input: game.input, step: (dt, now) => game.step(dt, now), view: (now) => game.view(now, round.fx()) },
    ({ t: _t, p, left: _left, fx: _fx, ...parts }) => ({ ...parts, p: p.map(([, kind, lean, wet, wilt]) => [kind, lean, wet >= 0, wilt >= 0]) }),
  );
  return {
    ms,
    keys,
    finger,
    steps: [
      { at: 0, en: 'PLANTS DRY OUT AND DROOP', no: 'PLANTENE TØRKER OG HENGER' },
      { at: 1_500, en: 'TAP ONE TO WATER IT', no: 'TRYKK PÅ EN FOR Å VANNE' },
      { at: 3_000, en: 'KEEP ALL ABOVE THE RED LINE', no: 'ALLE OVER DEN RØDE STREKEN' },
    ],
  };
}

export const game = thirtyGame<WaterView>({
  Board,
  View,
  demo: demoWater(),
  words: {
    en: {
      instruction: 'Tap a plant to water it. Keep every one above the red line until time is up.',
      howMove: 'Watch the gauges under the pots. Tap the plant that is drooping most.',
    },
    no: {
      instruction: 'Trykk på en plante for å vanne den. Hold alle over den røde streken til tiden er ute.',
      howMove: 'Følg målerne under pottene. Trykk på planten som henger mest.',
    },
  },
});
